'use client';

import React, {
    ButtonHTMLAttributes,
    forwardRef,
    JSXElementConstructor,
} from 'react';
import { IconName } from '../../tokens/icons/types';
import { Icon } from '../../tokens/icons';
import { capitalize } from '../../utilities';
import LinkManager, { LinkerComponent } from '../link-manager/LinkManager';
import cn from 'clsx';
import s from './Button.module.css';

export type ButtonVariant =
    | 'base'
    | 'ghost'
    | 'secondaryGhost'
    | 'secondary'
    | 'sidebar'
    | 'text'
    | 'quite'
    | 'quite_alt'
    | 'link_alt'
    | 'collection'
    | 'discover'
    | 'checkout'
    | 'buy'
    | 'active'
    | 'toggleActive'
    | 'toggleInactive';

export type ButtonRadiusSize = 'small' | 'medium' | 'large' | 'none';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    loading?: boolean;
    disabled?: boolean;
    Component?: string | JSXElementConstructor<any>;
    type?: 'submit' | 'reset' | 'button';
    active?: boolean;
    inactive?: boolean;
    radiusSize?: ButtonRadiusSize;
    className?: string;
    hrefLink?: string;
    icon?: IconName;
    iconUrl?: string;
    isIconNoMargin?: boolean | undefined;
    label?: string;
    textVariantPadding?: boolean;
    padding?: 'zeroInline' | 'zero';
    onClick?: (
        event?: React.MouseEvent<HTMLButtonElement>
    ) => void | Promise<void>;
    lang?: string;
    target?: string;
    theme?: 'light' | 'dark';
    Linker?: LinkerComponent;
    fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = forwardRef((props, buttonRef) => {
    const {
        variant = 'base',
        children,
        loading = false,
        disabled = false,
        Component = 'button',
        radiusSize = 'large',
        active,
        inactive = false,
        className,
        hrefLink,
        label,
        icon,
        iconUrl,
        isIconNoMargin,
        padding,
        textVariantPadding = true,
        onClick,
        theme = 'dark',
        lang,
        target,
        Linker = LinkManager,
        fullWidth = false,
        ...rest
    } = props;

    const rootClassName = cn(
        s.root,
        {
            [s.rootNortheast]: icon === 'northeast',
            [s.rootAlt]: variant === 'quite_alt' || variant === 'link_alt',
            [s.loading]: loading,
            // [s.active]: active && !disabled && !inactive,
            [s.inactive]: inactive && !disabled,
            [s.disabled]: disabled,
            [s[variant]]: !disabled && !inactive,
            [s[`disabled${capitalize(variant)}`]]: disabled,
            [s[`inactive${capitalize(variant)}`]]: inactive,
            [s[`active${capitalize(variant)}`]]:
                active && !disabled && !inactive,
            [s[`light${capitalize(variant)}`]]: theme === 'light',
            [s.radiusSmall]: radiusSize === 'small',
            [s.radiusMedium]: radiusSize === 'medium',
            [s.radiusLarge]: radiusSize === 'large',
            [s.radiusNone]: radiusSize === 'none',
            [s.paddingZeroInline]: padding === 'zeroInline',
            [s.paddingZero]: padding === 'zero',
            [s.textVariantSpacing]: variant === 'text' && !textVariantPadding,
            [s.fullWidth]: fullWidth,
        },
        className
    );

    return (
        <>
            {hrefLink && lang ? (
                <Linker
                    passthrough={!hrefLink}
                    href={hrefLink}
                    lang={lang}
                    target={target}
                    title={`Link for ${label}`}
                    className={s.buttonLink}
                >
                    <Component
                        aria-pressed={active}
                        data-variant={variant}
                        disabled={disabled}
                        className={rootClassName}
                        onClick={onClick}
                        {...rest}
                    >
                        {label}
                        {icon && (
                            <Icon
                                className={cn(
                                    s.icon,
                                    isIconNoMargin
                                        ? s.iconNoMargin
                                        : s.iconMargin
                                )}
                                icon={icon}
                            />
                        )}
                        {!icon && iconUrl && (
                            <img
                                src={iconUrl}
                                alt=""
                                aria-hidden="true"
                                className={cn(
                                    s.icon,
                                    s.iconImage,
                                    isIconNoMargin
                                        ? s.iconNoMargin
                                        : s.iconMargin
                                )}
                            />
                        )}
                    </Component>
                </Linker>
            ) : (
                <Component
                    aria-pressed={active}
                    data-variant={variant}
                    disabled={disabled}
                    className={rootClassName}
                    onClick={onClick}
                    {...rest}
                >
                    {label || children}
                    {icon && (
                        <Icon
                            className={cn(
                                s.icon,
                                isIconNoMargin ? s.iconNoMargin : s.iconMargin
                            )}
                            icon={icon}
                        />
                    )}
                    {!icon && iconUrl && (
                        <img
                            src={iconUrl}
                            alt=""
                            aria-hidden="true"
                            className={cn(
                                s.icon,
                                s.iconImage,
                                isIconNoMargin ? s.iconNoMargin : s.iconMargin
                            )}
                        />
                    )}
                </Component>
            )}
        </>
    );
});

Button.displayName = 'Button';
export default Button;
